import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Document_Role_Enum,
    Document_Status_Enum,
    Document_Type_Enum,
    DocumentFragment,
    Documents_Insert_Input,
    UpdateDocumentMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    OperationsAdminVenueDocumentCreateFragment, 
    OperationsAdminVenueDocumentCreateSchema, 
    OperationsAdminVenueDocumentUpdateFragment, 
    OperationsAdminVenueDocumentUpdateSchema 
} from "src/components/operations/administrator/documents/OperationsAdminVenueDocumentSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = OperationsAdminVenueDocumentCreateFragment

let initialState: FormPayloadType = {
    type: Document_Type_Enum.Document,
    url: "",
    filename: "",
    size: "",
    filetype: "",
};

export const CreateOperationsAdminVenueDocumentDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItems, updateItem, deleteItem } = useActions().documents;
    const { loading, errors } = useAppState().documents;
    const { tenant_id: currentTenantId } = useAppState(state => state.auth)
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Documents_Insert_Input) => {
        if (payload.url&&payload.url.length) {
            let newItems = JSON.parse(payload.url)
            let newItemsData = newItems.map((item: {url: string, filename: string, size: string, filetype: string}) => {
                return {
                    role: Document_Role_Enum.Venue,
                    venue_id: currentTenantId,
                    type: payload.type,
                    url: item.url,
                    filename: item.filename,
                    size: item.size,
                    filetype: item.filetype
                }
            })
            return createItems({ objects: newItemsData })
        }
        return Promise.resolve(null)
    }   

    return (
        <>
            <FormDrawer<FormPayloadType, DocumentFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueDocumentCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateOperationsAdminVenueDocumentAction = () => {
    return (
        <PageDrawerAction<DocumentFragment>
            title='Add Document'
            action={CreateOperationsAdminVenueDocumentDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: OperationsAdminVenueDocumentUpdateFragment = {
    status: Document_Status_Enum.Active,
};

export const UpdateOperationsAdminVenueDocumentDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().documents;
    const { currentItem, loading, errors } = useAppState().documents;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: OperationsAdminVenueDocumentUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<OperationsAdminVenueDocumentUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateDocumentMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<OperationsAdminVenueDocumentUpdateFragment, DocumentFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueDocumentUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateOperationsAdminVenueDocumentAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().documents;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.documents.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<DocumentFragment>
            title='Edit Document'
            action={UpdateOperationsAdminVenueDocumentDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditOperationsAdminVenueDocumentAction = () => {
    return (
        <PageDrawerAction<DocumentFragment>
            title='Edit Document'
            action={UpdateOperationsAdminVenueDocumentDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}